import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { invoiceApi } from '../lib/api';
import { Head } from '../components/Head';
import { toLocalizedFormat, formatRelativeTime } from '@dutchinvoice/shared-utils';
import type { Invoice, Language } from '@dutchinvoice/types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, AlertCircle, Bell, CreditCard, Globe, ChevronDown } from 'lucide-react';
import { paymentConfirmationTranslations } from '../i18n/translations/pages';
import { messages as enMessages } from '../i18n/translations/en';
import { messages as nlMessages } from '../i18n/translations/nl';
import { LANGUAGE_NAMES } from '../constants/languages';

// Define the simplified invoice status response structure
interface InvoiceStatusResponse {
  invoice: Pick<Invoice, 'id' | 'invoiceNumber' | 'date' | 'dueDate' | 'sender' | 'recipient' | 'paymentStatus' | 'paymentMethods'> & { 
    total: string;
    currency: string;
    paidAt?: number;
  };
  paymentLink?: string;
  paymentRedirectUrl?: string;
}

// Payment status icon mapping
const STATUS_ICONS = {
  paid: CheckCircle,
  unpaid: Clock,
  overdue: AlertCircle,
  overdue_reminded: Bell,
  canceled: Clock,
  failed: AlertCircle
};

const InvoiceStatusPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatusResponse | null>(null);
  
  // Ultra simple language override: get from localStorage or default to 'en'
  const savedLang = localStorage.getItem('invoice-status-lang') || 'en';
  const [currentLang, setCurrentLang] = useState<Language>(savedLang as Language);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const locale = currentLang; // Override the locale with our saved preference

  // Simple language switcher function
  const switchLanguage = (lang: Language) => {
    localStorage.setItem('invoice-status-lang', lang);
    setCurrentLang(lang);
    setIsLangDropdownOpen(false);
  };

  // Ultra simple translation function
  const t = (key: string): string => {
    const messages = currentLang === 'nl' ? nlMessages : enMessages;
    const keys = key.split('.');
    let value: unknown = messages;
    for (const k of keys) {
      value = (value as Record<string, unknown>)?.[k];
    }
    return (typeof value === 'string' ? value : key);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get payment confirmation messages based on current locale
  const getPaymentConfirmationMessage = (key: 'received' | 'thankyou') => {
    const messages = paymentConfirmationTranslations[locale as 'en' | 'nl'];
    return messages.confirmation[key];
  };

  useEffect(() => {
    const fetchInvoiceStatus = async () => {
      if (!id) {
        setError('Invalid invoice ID');
        setLoading(false);
        return;
      }

      try {
        const data = await invoiceApi.getInvoiceStatus(id);
        setInvoiceStatus(data);
      } catch (err) {
        console.error('Failed to fetch invoice status:', err);
        setError('Failed to load invoice information. The invoice may not exist or has expired.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceStatus();
  }, [id]);

  if (loading) {
    return (
      <div className="invoice-status-container min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="relative overflow-hidden backdrop-blur-sm bg-white/80 shadow-2xl rounded-2xl p-10 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-blue-100/30 z-0"></div>
            <div className="relative z-10">
              <div className="animate-pulse space-y-6">
                <div className="h-10 bg-gray-100 rounded-lg w-3/4 mx-auto"></div>
                <div className="h-48 bg-gray-100 rounded-xl"></div>
                <div className="h-14 bg-gray-100 rounded-xl w-1/2 mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invoiceStatus) {
    return (
      <div className="invoice-status-container min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="relative overflow-hidden backdrop-blur-sm bg-white/80 shadow-2xl rounded-2xl p-10 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/20 to-red-100/20 z-0"></div>
            <div className="relative z-10">
              <svg className="h-16 w-16 text-red-400 mx-auto mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="text-2xl font-medium text-gray-800 mb-3">{t('common.error')}</h2>
              <p className="text-gray-500 mb-8 font-light">{error || t('common.error')}</p>
              <Link 
                to="/" 
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-blue-600 bg-blue-50 rounded-full border border-blue-100 hover:bg-blue-100 transition-colors duration-200 ease-in-out"
              >
                {t('notFound.backHome')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { invoice, paymentLink } = invoiceStatus;
  const formattedAddress = (obj: { address?: string, city?: string, postalCode?: string }) => {
    const parts = [];
    if (obj.address) parts.push(obj.address);
    if (obj.postalCode || obj.city) {
      parts.push(`${obj.postalCode || ''} ${obj.city || ''}`.trim());
    }
    return parts;
  };

  const statusColorClasses: Record<string, string> = {
    'paid': 'bg-gradient-to-r from-green-50 to-green-100 text-green-700',
    'unpaid': 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700',
    'overdue': 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700',
    'overdue_reminded': 'bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700',
    'canceled': 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700',
    'failed': 'bg-gradient-to-r from-red-50 to-red-100 text-red-700'
  };

  // Map payment status to display text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return t('payment.status.paid');
      case 'unpaid':
        return t('payment.status.unpaid');
      case 'overdue':
        return t('payment.status.overdue');
      case 'canceled':
        return t('payment.status.canceled');
      case 'failed':
        return t('payment.status.failed');
      default:
        return t('payment.status.unpaid');
    }
  };

  // Get the status icon component
  const StatusIcon = STATUS_ICONS[invoice.paymentStatus as keyof typeof STATUS_ICONS] || Clock;
  const isPaid = invoice.paymentStatus === 'paid';

  return (
    <div className="invoice-status-container">
      <Head>
        <title>{t('invoice.title')} {invoice.invoiceNumber}</title>
      </Head>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16 px-4 sm:px-6 lg:px-8 relative">
        {/* Language Switcher - Top Right */}
        <div className="absolute top-6 right-6 z-50">
          <div ref={dropdownRef} className="relative">
            <motion.button
              onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 text-neutral-700 hover:bg-white/80 backdrop-blur-sm bg-white/60 shadow-sm border border-white/20"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Globe size={16} className="text-neutral-600" />
              <span className="font-medium">{LANGUAGE_NAMES[currentLang]}</span>
              <motion.div
                animate={{ rotate: isLangDropdownOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {isLangDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute right-0 mt-1 w-40 rounded-xl overflow-hidden z-50 backdrop-blur-sm bg-white/80 shadow-lg border border-white/20"
                >
                  <div className="py-1">
                    {Object.entries(LANGUAGE_NAMES).map(([lang, name]) => (
                      <motion.button
                        key={lang}
                        onClick={() => switchLanguage(lang as Language)}
                        className="flex w-full items-center px-4 py-2.5 text-sm text-left transition-colors text-neutral-700 hover:bg-white/60"
                        whileHover={{ x: 2 }}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium">{name}</span>
                          {currentLang === lang && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="h-2 w-2 rounded-full bg-neutral-900"
                            />
                          )}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="relative overflow-hidden backdrop-blur-sm bg-white/90 shadow-2xl rounded-2xl animate-fade-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-8 py-6 rounded-t-2xl">
              <div className="flex items-center justify-between text-2xl">
                <h1 className="text-white font-medium tracking-tight">{t('invoice.title')}</h1>
                <div className="text-white font-medium">
                  {invoice.invoiceNumber}
                </div>
              </div>
            </div>
            
            {/* Payment Confirmation (if paid) */}
            <AnimatePresence>
              {isPaid && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="relative w-full overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-green-50/80 to-emerald-50/50"></div>
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-200/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-200/50 to-transparent"></div>
                  
                  <div className="relative py-12 px-8">
                    <div className="flex flex-col items-center">
                      <motion.div 
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ 
                          type: "spring",
                          delay: 0.1,
                          duration: 0.7
                        }}
                        className="relative mb-6"
                      >
                        <div className="absolute inset-0 rounded-full bg-green-100 blur-lg opacity-60 scale-110 transform-gpu"></div>
                        <div className="relative flex items-center justify-center w-[70px] h-[70px] rounded-full bg-gradient-to-br from-green-50 to-white border border-green-100/70 shadow-lg" 
                             style={{ boxShadow: '0 15px 30px -10px rgba(22, 163, 74, 0.3)' }}>
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring", bounce: 0.4 }}
                          >
                            <CheckCircle size={34} className="text-green-500" strokeWidth={2} />
                          </motion.div>
                        </div>
                      </motion.div>
                      
                      <motion.div
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-center"
                      >
                        <h2 className="text-xl font-medium text-green-800 mb-2 tracking-tight">
                          {getPaymentConfirmationMessage('received')}
                        </h2>
                        
                        <p className="text-base text-green-700 max-w-sm mx-auto">
                          {getPaymentConfirmationMessage('thankyou')}
                        </p>
                      </motion.div>
                      
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="mt-7 px-6 py-2.5 rounded-full text-sm font-medium text-green-700 bg-white/80 backdrop-blur-sm border border-green-100 shadow-sm"
                        style={{ boxShadow: '0 2px 10px -2px rgba(22, 163, 74, 0.15)' }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
                          <span>{getStatusText(invoice.paymentStatus)}</span>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Content */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                {/* From Section */}
                <div className="transition-all duration-300 hover:transform hover:scale-[1.02]">
                  <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
                    {t('invoice.from')}
                  </h2>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <p className="text-xl font-medium text-gray-800 mb-2">{invoice.sender.company}</p>
                    {formattedAddress(invoice.sender).map((line, i) => (
                      <p key={i} className="text-gray-500 font-light">{line}</p>
                    ))}
                  </div>
                </div>

                {/* To Section */}
                <div className="transition-all duration-300 hover:transform hover:scale-[1.02]">
                  <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
                    {t('invoice.to')}
                  </h2>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <p className="text-xl font-medium text-gray-800 mb-2">{invoice.recipient.company}</p>
                    {formattedAddress(invoice.recipient).map((line, i) => (
                      <p key={i} className="text-gray-500 font-light">{line}</p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="mb-10 rounded-xl overflow-hidden backdrop-blur-sm bg-white/80 shadow-sm border border-gray-100">
                <div className="divide-y divide-gray-100">
                  <div className="flex justify-between items-center p-5">
                    <span className="text-gray-500 font-light">{t('invoice.date')}</span>
                    <span className="text-gray-800 font-medium">{toLocalizedFormat(invoice.date, locale)}</span>
                  </div>
                  {/* Due Date */}
                  {invoice.dueDate && (
                    <div className="flex justify-between items-center p-5">
                      <span className="text-gray-500 font-light">{t('invoice.dueDate')}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-800 font-medium">{toLocalizedFormat(invoice.dueDate, locale)}</span>
                        {(invoice.paymentStatus === 'overdue' || invoice.paymentStatus === 'overdue_reminded') && (
                          <AlertCircle size={16} className="text-amber-500" />
                        )}
                      </div>
                    </div>
                  )}
                  {/* Payment date - only show if paid */}
                  {isPaid && invoice.paidAt && (
                    <div className="flex justify-between items-center p-5">
                      <span className="text-gray-500 font-light">{t('invoice.paymentDate')}</span>
                      <div className="flex flex-col items-end">
                        <span className="text-gray-800 font-medium">
                          {formatRelativeTime(invoice.paidAt, locale)}
                        </span>
                        <span className="text-gray-500 text-sm mt-1">
                          {toLocalizedFormat(new Date(invoice.paidAt).toISOString().split('T')[0], locale)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center p-5 bg-gray-50/50">
                    <span className="text-gray-500 font-light">{t('invoice.total')}</span>
                    <span className="text-3xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800">
                      {invoice.total} {invoice.currency}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Status - Only show if not paid */}
              {!isPaid && (
                <div className="rounded-xl overflow-hidden backdrop-blur-sm bg-white/80 shadow-sm border border-gray-100 p-6 mb-10">
                  <h3 className="text-sm font-medium text-gray-500 mb-4">{t('invoice.status')}</h3>
                  <motion.div 
                    className="flex items-center mb-6"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.span 
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${statusColorClasses[invoice.paymentStatus] || statusColorClasses.unpaid} shadow-sm`}
                      whileHover={{ scale: 1.03 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <StatusIcon size={16} />
                      {getStatusText(invoice.paymentStatus)}
                    </motion.span>
                  </motion.div>

                  {/* Bank Payment Details (if bank payment is enabled) */}
                  {invoice.paymentMethods?.bank?.enabled && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                      className="mb-6 relative overflow-hidden"
                    >
                      {/* Elegant background with subtle gradients */}
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 rounded-2xl"></div>
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20 rounded-2xl"></div>
                      <div className="absolute inset-[1px] bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20"></div>
                      
                      {/* Content */}
                      <div className="relative p-6">
                        {/* Header with icon */}
                        <div className="flex items-center gap-3 mb-6">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-semibold text-slate-800 tracking-tight">{t('invoice.paymentMethods.bank.title')}</h4>
                        </div>

                        {/* Payment details grid */}
                        <div className="space-y-5">
                          {/* Account Name */}
                          <div className="group">
                            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 transition-colors group-hover:text-slate-600">
                              {t('invoice.paymentMethods.bank.accountName')}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-0 bg-gradient-to-r from-slate-100/50 to-slate-50/50 rounded-xl"></div>
                              <p className="relative px-4 py-3 text-slate-800 font-medium bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/60 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:border-slate-300/60">
                                {invoice.paymentMethods.bank.accountName}
                              </p>
                            </div>
                          </div>

                          {/* IBAN */}
                          <div className="group">
                            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 transition-colors group-hover:text-slate-600">
                              {t('invoice.paymentMethods.bank.iban')}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-xl"></div>
                              <p className="relative px-4 py-3 text-slate-800 font-mono text-sm tracking-wider bg-white/90 backdrop-blur-sm rounded-xl border border-blue-200/60 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:border-blue-300/60 group-hover:bg-blue-50/30">
                                {invoice.paymentMethods.bank.iban}
                              </p>
                            </div>
                          </div>

                          {/* BIC/SWIFT (if available) */}
                          {invoice.paymentMethods.bank.bic && invoice.paymentMethods.bank.bic.trim() !== '' && (
                            <div className="group">
                              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 transition-colors group-hover:text-slate-600">
                                BIC/SWIFT
                              </label>
                              <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-slate-100/50 to-slate-50/50 rounded-xl"></div>
                                <p className="relative px-4 py-3 text-slate-800 font-mono text-sm tracking-wider bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/60 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:border-slate-300/60">
                                  {invoice.paymentMethods.bank.bic}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Reference */}
                          <div className="group">
                            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 transition-colors group-hover:text-slate-600">
                              {t('invoice.paymentMethods.bank.reference')}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/50 to-green-50/50 rounded-xl"></div>
                              <p className="relative px-4 py-3 text-slate-800 font-semibold bg-white/90 backdrop-blur-sm rounded-xl border border-emerald-200/60 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:border-emerald-300/60 group-hover:bg-emerald-50/30">
                                {invoice.invoiceNumber}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Subtle instruction text */}
                        <div className="mt-6 pt-4 border-t border-slate-200/60">
                          <p className="text-xs text-slate-500 text-center font-light leading-relaxed">
                            {t('invoice.paymentMethods.bank.includeReference')}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Payment Button (if unpaid/overdue and has Mollie payment link) */}
                  {(invoice.paymentStatus === 'unpaid' || invoice.paymentStatus === 'overdue' || invoice.paymentStatus === 'overdue_reminded') && invoice.paymentMethods?.mollie?.enabled && (paymentLink || invoiceStatus.paymentRedirectUrl) && (
                    <motion.a
                      href={invoiceStatus.paymentRedirectUrl || paymentLink}
                      className="flex justify-center items-center gap-2 w-full px-8 py-4 border-0 text-base font-medium rounded-full text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg transform transition-all duration-200 hover:translate-y-[-1px] active:translate-y-[1px]"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <CreditCard size={20} />
                      {t('invoice.paymentMethods.payNow')}
                    </motion.a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-12 text-gray-400 text-sm font-light">
            <p>© {new Date().getFullYear()} - InvoiceHappens.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceStatusPage; 